/* eslint-disable react/prop-types */
import React from 'react';
import { GridColDef, GridRenderEditCellParams } from '@mui/x-data-grid';
import { format, formatCurrency, formatPercentage } from '../../common/utils/common.util';
import {
  calculateUnitCost,
  getNotationColor,
  getNotations,
  calculateDeviationPercentage,
  calculateCdccCompTotal,
  calculateDeviation,
  isTotalRow,
} from './report.util';
import { NEGATIVE_NUMBER_COLOR } from '../../common/constants/general';
import { ALLOWANCES_TOTAL_ID, TOTAL_ID } from './constants';
import { Autocomplete, TextField, Tooltip, TooltipProps, tooltipClasses } from '@mui/material';
import {
  LabelType,
  ReportLineItemTypes,
  labelColors,
  CAReportLineItemValidationSchema,
  ReportLineItemNotations,
  ReportLineItem,
} from './report.type';
import NumericFormatInput from '../general/NumericFormatInput';
import styled from '@emotion/styled';
import theme from 'assets/theme';
import SelectInput from '../SelectInput';
import { LineItemUnitEnum } from '../glossary/glossary.type';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import DragCell from '../../common/DragCell';
import { Edit } from '@mui/icons-material';
import { Box } from 'components/index';

const unitsList = [...Object.values(LineItemUnitEnum).map(unit => ({ value: unit, label: unit })), { label: 'None', value: '' }];
const notationsList = [...Object.values(ReportLineItemNotations).map(notation => ({ value: notation, label: notation }))];

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.error.main,
  },
}));

const renderColCell = (params: any) => {
  const isAdjustment = params.row.type === ReportLineItemTypes.Adjustment;
  const value = params.value as number;
  const formattedValue = typeof value === 'number' ? formatCurrency(value.toString(), true) : '';
  const isAllowance = params.row.label === 'Allowances';
  const isTotalBudgetField = params.field === 'totalBudget';
  const color =
    !isAdjustment && value < 0
      ? NEGATIVE_NUMBER_COLOR
      : isAllowance && isTotalBudgetField
        ? labelColors[params.row.label as LabelType]
        : 'inherit';
  return (
    <div className='MuiDataGrid-cellContent' style={{ color, height: '100%', display: 'flex', alignItems: 'center' }}>
      {formattedValue}
    </div>
  );
};

const CustomOriginalBudgetEditCell = (params: GridRenderEditCellParams) => {
  const { error } = params;
  const ref = React.useRef<any>();

  React.useLayoutEffect(() => {
    if (params.hasFocus) {
      ref.current?.focus();
    }
  }, [params.hasFocus]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    params.api.setEditCellValue({ id: params.id, field: params.field, value: e.target.value });
  };

  const handleBlur = () => {
    params.api.stopCellEditMode({
      id: params.id,
      field: params.field,
      ignoreModifications: Boolean(error),
    });
  };

  return (
    <StyledTooltip open={!!error} title={error}>
      <div>
        <TextField
          value={params.value}
          onChange={handleChange}
          onBlur={handleBlur}
          variant='standard'
          InputProps={{
            inputComponent: NumericFormatInput as any,
          }}
          inputProps={{ ref: ref }}
          sx={{ height: '100%', justifyContent: 'center' }}
        />
      </div>
    </StyledTooltip>
  );
};

const CustomUnitEditCell = (params: GridRenderEditCellParams) => {
  const { error } = params;
  const ref = React.useRef<any>();

  React.useLayoutEffect(() => {
    if (params.hasFocus) {
      ref.current?.focus();
    }
  }, [params.hasFocus]);

  const handleBlur = () => {
    params.api.stopCellEditMode({
      id: params.id,
      field: params.field,
      ignoreModifications: Boolean(error),
    });
  };

  return (
    <StyledTooltip open={!!error} title={error}>
      <div>
        <SelectInput
          selected={params.value ?? ''}
          setSelected={newVal => {
            params.api.setEditCellValue({ id: params.id, field: params.field, value: newVal === '' ? null : newVal });
          }}
          list={unitsList}
          inputProps={{ ref: ref }}
          onBlur={handleBlur}
          sx={{ height: '100%', justifyContent: 'center', width: '100%' }}
        />
      </div>
    </StyledTooltip>
  );
};

const CustomQuantityEditCell = (params: GridRenderEditCellParams) => {
  const { error } = params;
  const ref = React.useRef<any>();

  React.useLayoutEffect(() => {
    if (params.hasFocus) {
      ref.current?.focus();
    }
  }, [params.hasFocus]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    params.api.setEditCellValue({ id: params.id, field: params.field, value: e.target.value });
  };

  const handleBlur = () => {
    params.api.stopCellEditMode({
      id: params.id,
      field: params.field,
      ignoreModifications: Boolean(error),
    });
  };

  return (
    <StyledTooltip open={!!error} title={error}>
      <div>
        <TextField
          value={params.value}
          onChange={handleChange}
          onBlur={handleBlur}
          variant='standard'
          InputProps={{
            inputComponent: NumericFormatInput as any,
          }}
          inputProps={{ ref: ref }}
          sx={{ height: '100%', justifyContent: 'center' }}
        />
      </div>
    </StyledTooltip>
  );
};

const CustomCdccCompPerUnitEditCell = (params: GridRenderEditCellParams) => {
  const { error } = params;
  const ref = React.useRef<any>();

  React.useLayoutEffect(() => {
    if (params.hasFocus) {
      ref.current?.focus();
    }
  }, [params.hasFocus]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    params.api.setEditCellValue({ id: params.id, field: params.field, value: e.target.value });
  };
  const handleBlur = () => {
    params.api.stopCellEditMode({
      id: params.id,
      field: params.field,
      ignoreModifications: Boolean(error),
    });
  };

  const isTotal =
    params.row.type != null && params.row.type !== ReportLineItemTypes.NORMAL && params.row.type !== ReportLineItemTypes.CompPricing;
  const isPercentageDivision = ['1.00', '17.00', '18.00'].includes(params.row.division?.code);
  const showPrefix = !(isTotal && isPercentageDivision);

  return (
    <StyledTooltip open={!!error} title={error}>
      <div>
        <TextField
          value={params.value}
          onChange={handleChange}
          onBlur={handleBlur}
          variant='standard'
          InputProps={{
            inputComponent: NumericFormatInput as any,
          }}
          inputProps={{
            prefix: showPrefix ? '$' : '',
            ref: ref,
          }}
          sx={{ height: '100%', justifyContent: 'center' }}
        />
      </div>
    </StyledTooltip>
  );
};

const CustomNotationsEditCell = (params: GridRenderEditCellParams) => {
  const ref = React.useRef<any>();

  React.useLayoutEffect(() => {
    if (params.hasFocus) {
      ref.current?.focus();
    }
  }, [params.hasFocus]);

  return (
    <Autocomplete
      freeSolo
      id='notation'
      options={notationsList}
      inputValue={params.value ?? ''}
      onInputChange={(_, newVal) => {
        params.api.setEditCellValue({ id: params.id, field: params.field, value: newVal });
      }}
      fullWidth
      renderInput={params => <TextField {...params} name='notation' variant='standard' inputRef={ref} />}
      sx={{ height: '100%', justifyContent: 'center' }}
    />
  );
};

const DRAG_TYPE = 'LINE_ITEM';

export const RenderDescriptionCellWithDrag = (params: any, getSelectedLineItems?: () => ReportLineItem[]) => {
  const [{ isDragging }, dragRef, preview] = useDrag({
    type: DRAG_TYPE,
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
    item: () => {
      const selectedLineItems = getSelectedLineItems?.() || [];
      const isRowSelected = selectedLineItems.some(li => li.id === params.row.id);
      const payload = {
        id: params.row.id,
        divisionId: params.row.division?.id,
        selectedRows: selectedLineItems.length > 1 && isRowSelected ? selectedLineItems : [params.row],
      };
      return payload;
    },
  });

  React.useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  return <DragCell params={params} isDragging={isDragging} dragRef={dragRef} />;
};

export const renderDescriptionCellDefault = (params: any) => {
  return RenderDescriptionCellWithDrag(params, () => []);
};

export const getReportColumns: (
  renderActionsHeader: ((params: any) => JSX.Element | null) | null,
  renderActionsCell: ((params: any) => JSX.Element | null) | null,
  onlyProjectCols?: boolean,
  includeActionsCol?: boolean,
  showRevisedBudget?: boolean,
  renderDescriptionCell?: (params: any, getSelectedLineItems?: () => ReportLineItem[]) => JSX.Element,
) => GridColDef[] = (
  renderActionsHeader,
  renderActionsCell,
  onlyProjectCols = false,
  includeActionsCol = true,
  showRevisedBudget = false,
  renderDescriptionCell = renderDescriptionCellDefault,
) => {
  const projectCols: GridColDef[] = [
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 250,
      headerClassName: 'projectHeader leftBorder',
      cellClassName: 'projectTotal leftBorder bolderTotalsDescription',
      renderCell: renderDescriptionCell,
    },
    {
      field: showRevisedBudget ? 'originalBudget' : 'totalBudget',
      headerName: showRevisedBudget ? 'Original Budget' : 'Total Budget',
      type: 'number',
      flex: 1,
      minWidth: 120,
      valueGetter: (_value, row) => {
        return showRevisedBudget ? row.originalBudget : row.totalBudget;
      },
      renderCell: renderColCell,
      editable: true,
      preProcessEditCellProps: async params => {
        try {
          const field = showRevisedBudget ? 'originalBudget' : 'totalBudget';
          await CAReportLineItemValidationSchema.validateAt(field, { [field]: params.props.value });
          return { ...params.props, error: null };
        } catch (error: any) {
          return { ...params.props, error: error.message };
        }
      },
      renderEditCell: (params: GridRenderEditCellParams) => {
        return <CustomOriginalBudgetEditCell {...params} />;
      },
      headerClassName: 'projectHeader',
      cellClassName: 'projectTotal',
    },
    ...(showRevisedBudget
      ? [
          {
            field: 'revisedBudget',
            headerName: 'Revised Budget',
            type: 'number' as const,
            flex: 1,
            minWidth: 120,
            valueGetter: (value: any, row: any, column: any, apiRef: any) => {
              if (row.id === TOTAL_ID) return row.revisedBudget;
              return row.originalBudget + row.reallocation + row.changeOrder;
            },
            renderCell: renderColCell,
            headerClassName: 'projectHeader',
            cellClassName: 'projectTotal',
          },
        ]
      : []),
    {
      field: 'quantity',
      headerName: 'Quantity',
      type: 'number',
      flex: 1,
      minWidth: 120,
      editable: true,
      preProcessEditCellProps: async params => {
        try {
          const field = 'quantity';
          await CAReportLineItemValidationSchema.validateAt(field, { [field]: params.props.value });
          return { ...params.props, error: null };
        } catch (error: any) {
          return { ...params.props, error: error.message };
        }
      },
      renderEditCell: params => <CustomQuantityEditCell {...params} />,
      valueFormatter: (value: any, row, column, apiRef) => {
        if (!row?.id) return value;

        const divisionCode = row?.division?.code;
        const divisionName = row?.lineItem?.description;
        const isPercentageDivision =
          ['1.00', '17.00', '18.00'].includes(divisionCode) ||
          [
            'GENERAL CONDITIONS TOTALS',
            'GENERAL CONTRACTOR FEES / OVERHEAD & PROFIT TOTALS',
            'GENERAL CONTRACTOR CONTINGENCY TOTALS',
          ].includes(divisionName);

        const isTotal = isTotalRow(row);

        if (isPercentageDivision && isTotal) return formatPercentage(value?.toString());
        if (row.id === TOTAL_ID || row?.type === 'DivisionTotal') return formatCurrency(value?.toString(), true);
        if (row.id === ALLOWANCES_TOTAL_ID) return formatPercentage(value?.toString());

        return value ? format(value, 'number') : null;
      },
      headerClassName: 'projectHeader',
      cellClassName: 'projectTotal',
    },
    {
      field: 'unit',
      headerName: 'Unit',
      flex: 1,
      minWidth: 100,
      editable: true,
      renderEditCell: params => <CustomUnitEditCell {...params} />,
      valueGetter: (_value, row) => row.unit || row.lineItem?.unit,
      headerClassName: 'projectHeader',
      cellClassName: 'projectTotal',
    },
    {
      field: 'unitCost',
      headerName: '$ / Unit',
      type: 'number',
      flex: 1,
      minWidth: 120,
      valueGetter: (_value, row) => calculateUnitCost(row),
      valueFormatter: (value: any) => (value && value !== 0 ? formatCurrency(value?.toString()) : '--'),
      headerClassName: 'projectHeader',
      cellClassName: 'projectTotal',
    },
  ];

  const reportCols: GridColDef[] = [
    ...projectCols,
    {
      field: 'cdccCompPerUnit',
      headerName: 'CDCC Comp / Unit',
      type: 'number',
      flex: 1,
      minWidth: 120,
      editable: true,
      renderHeader: () => <div style={{ whiteSpace: 'normal', lineHeight: '1.2', textAlign: 'center' }}>CDCC Comp / Unit</div>,
      preProcessEditCellProps: async params => {
        try {
          const field = 'cdccCompPerUnit';
          await CAReportLineItemValidationSchema.validateAt(field, { [field]: params.props.value });
          return { ...params.props, error: null };
        } catch (error: any) {
          return { ...params.props, error: error.message };
        }
      },
      renderEditCell: params => <CustomCdccCompPerUnitEditCell {...params} />,
      renderCell: params => {
        let formattedValue = params.value;
        const isTotal =
          params.row.type != null &&
          params.row.type !== ReportLineItemTypes.NORMAL &&
          params.row.type !== ReportLineItemTypes.CompPricing &&
          params.row.type !== ReportLineItemTypes.Adjustment;
        const isPercentageDivision = ['1.00', '17.00', '18.00'].includes(params.row.division?.code);
        const isAllowances = params.row.id === 'allowances total';
        const isEditableDivisionTotal = isTotal && (params.row.division?.code === '1.00' || params.row.division?.code === '17.00');

        if ((isTotal && isPercentageDivision) || isAllowances) {
          formattedValue = formatPercentage(params.value?.toString());
        } else {
          formattedValue = formatCurrency(params.value?.toString(), true);
        }

        return (
          <Box display='flex' alignItems='center' justifyContent='space-between' width='100%' height='100%'>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {formattedValue}
            </Box>
            {isEditableDivisionTotal && (
              <Tooltip title='Double click to edit' arrow placement='top'>
                <Edit
                  fontSize='small'
                  sx={{
                    color: 'primary.main',
                    opacity: 0.7,
                    marginLeft: '8px',
                  }}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
      headerClassName: 'compHeader',
      cellClassName: 'compTotal',
    },
    {
      field: 'cdccCompTotal',
      headerName: 'CDCC Comp Total',
      type: 'number',
      flex: 1,
      minWidth: 120,
      renderHeader: () => <div style={{ whiteSpace: 'normal', lineHeight: '1.2', textAlign: 'center' }}>CDCC Comp Total</div>,
      valueGetter: (_value, row) => calculateCdccCompTotal(row),
      valueFormatter: value => (value !== null && value !== undefined ? formatCurrency(value as string) : '--'),
      headerClassName: 'compHeader',
      cellClassName: 'compTotal',
    },
    {
      field: 'deviation',
      headerName: 'Deviation',
      type: 'number',
      flex: 1,
      minWidth: 120,
      valueGetter: (_value, row) => calculateDeviation(row),
      valueFormatter: value => (value !== null && value !== undefined ? formatCurrency(value as string) : '--'),
      headerClassName: 'compHeader',
      cellClassName: 'compTotal',
    },
    {
      field: 'deviationPercentage',
      headerName: '% Deviation',
      type: 'number',
      flex: 1,
      minWidth: 100,
      valueGetter: (_value, row) =>
        row.type != null && row.type !== ReportLineItemTypes.NORMAL ? calculateDeviationPercentage(row) : null,
      valueFormatter: value => (value !== null && value !== undefined ? formatPercentage((value * 100).toString(), true) : '--'),
      headerClassName: 'compHeader',
      cellClassName: 'compTotal',
    },
    {
      field: 'notations',
      headerName: 'Notations',
      flex: 1,
      minWidth: 160,
      valueGetter: (_value, row) =>
        row.type != null && row.type !== ReportLineItemTypes.NORMAL ? (row.notation ?? getNotations(row)) : row.notation,
      valueSetter: (value, row) => {
        return { ...row, notation: value };
      },
      renderCell: params => <span style={{ color: getNotationColor(params.value) }}>{params.value}</span>,
      headerClassName: 'compHeader',
      cellClassName: 'compTotal',
      editable: true,
      renderEditCell: params => <CustomNotationsEditCell {...params} />,
    },
  ];
  const actionsCol: GridColDef = {
    field: 'actions',
    headerName: '',
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    minWidth: 120,
    flex: 0,
    align: 'right',
    renderHeader: renderActionsHeader || undefined,
    renderCell: renderActionsCell || undefined,
  };
  let cols = onlyProjectCols ? projectCols : reportCols;
  if (includeActionsCol) cols = [...cols, actionsCol];
  return cols;
};

export const descriptionCellStyles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column', // This is a valid value for flexDirection
    width: '100%',
    minWidth: 0,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    width: '100%',
    minWidth: 0,
  },
  description: {
    marginRight: '18px',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'inline-block',
    flex: '1 1 auto',
  },
  noteContainer: {
    marginTop: '10px',
    padding: '3px 10px',
    backgroundColor: '#f9f9f9',
    borderRadius: '10px',
    border: '1px solid #ccc',
    width: '100%',
    height: '30px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    lineHeight: '20px',
  },
  badgeContainer: {
    display: 'inline-flex',
    position: 'relative',
  },
};
